// The live event stream (docs/CONTRACTS.md section 8): POST /api/events with body {} answers NDJSON. Events carry
// ids only; on a change the client refetches through the API. Query-key convention for features: the first element
// is the entity kind ('org', 'touch', 'note', …) or the key contains the entity id, so a change invalidates it; a kind
// may also name other keys it refreshes (REFRESHES). Changes that arrive together invalidate once.
import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { ChangeEvent, StreamEvent } from '@nw/shared/event'
import { ApiError, apiResponse } from './api.ts'
import { readNdjson } from './ndjson.ts'

function isStreamEvent(v: unknown): v is StreamEvent {
  const type = (v as { type?: unknown } | null)?.type
  return type === 'hello' || type === 'ping' || type === 'change'
}

const sleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    const t = window.setTimeout(resolve, ms)
    signal.addEventListener('abort', () => (window.clearTimeout(t), resolve()), { once: true })
  })

/**
 * Keeps the stream open until `signal` aborts: reconnects after a drop with a backoff of 1 s doubling to 30 s
 * (reset by each hello). Stops for good on 401 or 403 (the API's onUnauthenticated hook takes over).
 */
export async function runEventStream(onEvent: (e: StreamEvent) => void, signal: AbortSignal): Promise<void> {
  let backoff = 1000
  while (!signal.aborted) {
    try {
      const res = await apiResponse('/api/events', { method: 'POST', body: {}, signal, headers: { accept: 'application/x-ndjson' } })
      if (!res.body) throw new ApiError(res.status, 'bad_response', 'no stream body')
      for await (const value of readNdjson(res.body, signal)) {
        if (!isStreamEvent(value)) continue
        if (value.type === 'hello') backoff = 1000
        onEvent(value)
      }
    } catch (e) {
      if (signal.aborted) return
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) return
    }
    await sleep(backoff, signal)
    backoff = Math.min(backoff * 2, 30_000)
  }
}

/**
 * Queries a change of this kind also refreshes, as key prefixes, beyond what keyMatches finds on its own. Today's queue
 * is keyed ['touch', 'today'], which neither a task nor an objection event would name.
 */
export const REFRESHES: Partial<Record<ChangeEvent['kind'], readonly (readonly string[])[]>> = {
  task: [['touch', 'today']],
  objection: [['touch', 'today']],
}

/** True when a query key names the changed entity: its kind first, its id anywhere, or a prefix REFRESHES declares. */
export function keyMatches(key: readonly unknown[], e: ChangeEvent): boolean {
  return key[0] === e.kind || key.includes(e.entityId) || !!REFRESHES[e.kind]?.some((prefix) => prefix.every((p, i) => key[i] === p))
}

/** Changes that arrive within this long of each other invalidate once: a bulk operation emits one event per row. */
const COALESCE_MS = 100

/** Opens the stream while `enabled` and invalidates the queries the changes of a short window touch, once. */
export function useEventStream(enabled: boolean): void {
  const queryClient = useQueryClient()
  useEffect(() => {
    if (!enabled) return
    const controller = new AbortController()
    let pending: ChangeEvent[] = []
    let timer = 0
    const flush = () => {
      const events = pending
      pending = []
      timer = 0
      void queryClient.invalidateQueries({ predicate: (q) => events.some((e) => keyMatches(q.queryKey, e)) })
    }
    void runEventStream((e) => {
      if (e.type !== 'change') return
      pending.push(e)
      timer ||= window.setTimeout(flush, COALESCE_MS)
    }, controller.signal)
    return () => {
      controller.abort()
      window.clearTimeout(timer)
    }
  }, [enabled, queryClient])
}
